import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute } from "@angular/router";
import {
  MarkerClusterer,
  SuperClusterAlgorithm,
  Renderer,
} from "@googlemaps/markerclusterer";
import { Subscription } from "rxjs";
import { BrandFacebookPostLog } from "../../../shared/models/brandFacebookPostLog";
import { BrandGoogleAdPostLog } from "../../../shared/models/brandGoogleAdPostLog";
import { BrandCampaign } from "../../../shared/models/brandCampaign";
import { PostLogStatus } from "../../../shared/enums/campaignPost.enums";
import { BrandCampaignDialogService } from "../brand-campaign-dialog.service";
import {
  BrandPostLog,
  BrandPostLogWithAd,
} from "../../../shared/types/postLog.types";
import {
  BrandCampaignPostLogService,
  PostLogsFilters,
} from "../shared/services/brand-campaign-post-log.service";
import { BrandCampaignService } from "../shared/services/brand-campaign.service";
import { assert } from "../../../shared/utils/assert";

@Component({
  selector: "app-brand-campaign-map",
  templateUrl: "./brand-campaign-map.component.html",
  styleUrl: "./brand-campaign-map.component.scss",
})
export class BrandCampaignMapComponent implements OnInit, OnDestroy {
  @ViewChild("gmap") protected gmap!: ElementRef<HTMLDivElement>;

  protected loading = true;

  private readonly limit = 1000;
  private readonly subscriptions = new Subscription();
  private campaign: BrandCampaign;
  private map?: google.maps.Map;
  private offset = 0;
  private postLogs: BrandPostLog[] = [];

  constructor(
    private readonly brandCampaignService: BrandCampaignService,
    private readonly dialog: MatDialog,
    private readonly brandCampaignDialogService: BrandCampaignDialogService,
    private readonly brandCampaignPostLogService: BrandCampaignPostLogService,
    private readonly activatedRoute: ActivatedRoute,
  ) {
    this.campaign = this.brandCampaignService.currentBrandCampaign;
  }

  public ngOnInit(): void {
    this.loadAds();
  }

  public ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }

  private loadAds(): void {
    const filters: Partial<PostLogsFilters> = {
      googleAds: true,
      facebookAds: true,
      status: PostLogStatus.Published,
    };

    this.loading = true;

    this.brandCampaignPostLogService
      .getBrandCampaignPostLogs(
        this.brandCampaignService.currentBrandCampaignId,
        filters,
        this.offset,
        this.limit,
      )
      .subscribe((list) => {
        // This cast is necessary because the previous call is filtering out the LinkedIn post logs
        this.postLogs = [...this.postLogs, ...list.items] as (
          | BrandFacebookPostLog
          | BrandGoogleAdPostLog
        )[];

        if (list.items.length > 0 && list.totalCount > this.postLogs.length) {
          this.offset = this.offset + this.limit;
          this.loadAds();
        } else {
          this.loading = false;
          this.setUpMap();
        }
      });
  }

  private setUpMap(): void {
    const mapRef = this.gmap.nativeElement;
    const bounds = new google.maps.LatLngBounds();
    const mapSettings: google.maps.MapOptions = {
      center: new google.maps.LatLng(0, 0),
      zoom: 14,
      mapTypeId: google.maps.MapTypeId.ROADMAP,
    };
    this.map = new google.maps.Map(mapRef, mapSettings);
    const markers: google.maps.Marker[] = [];

    this.postLogs.forEach((postLog) => {
      if (!postLog.ad) {
        return;
      }

      const markerLatLng = new google.maps.LatLng(
        postLog.ad.latitude,
        postLog.ad.longitude,
      );

      const image: google.maps.Icon = {
        url: "/assets/images/as_ad-marker.png",
        size: new google.maps.Size(100, 100),
        scaledSize: new google.maps.Size(30, 30),
        origin: new google.maps.Point(0, 0),
        anchor: new google.maps.Point(0, 25),
      };

      const marker = new google.maps.Marker({
        position: markerLatLng,
        animation: google.maps.Animation.DROP,
        map: this.map,
        icon: image,
      });

      marker.addListener("click", () => {
        this.openAd(postLog);
      });

      markers.push(marker);
    });

    const renderer: Renderer = {
      render: ({ count, position }) => {
        const imageIndex = Math.min(Math.floor((count - 1) / 6) + 1, 5);
        const iconUrl = `/assets/images/icons/google-maps/sp-m${imageIndex}.png`;

        return new google.maps.Marker({
          position: position,
          icon: {
            url: iconUrl,
            anchor: new google.maps.Point(26, 30),
          },
          label: {
            text: String(count),
            color: "black",
            fontSize: "11px",
            fontFamily: "Arial,sans-serif",
            fontWeight: "bold",
          },
          zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
        });
      },
    };

    // tslint:disable-next-line:no-unused-expression
    new MarkerClusterer({
      map: this.map,
      algorithm: new SuperClusterAlgorithm({
        radius: 140,
        maxZoom: 13,
      }),
      markers: markers,
      renderer: renderer,
      onClusterClick: (_, cluster) => {
        assert(this.map);

        if (cluster.bounds) {
          this.map.fitBounds(cluster.bounds);
        }

        const currentZoom = this.map.getZoom();

        if (currentZoom && currentZoom > 14) {
          this.map.setZoom(14);
        }
      },
    });

    this.map.fitBounds(bounds);
  }

  private openAd(postLog: BrandPostLog): void {
    if (!postLog.ad) {
      return;
    }

    this.dialog.closeAll();
    this.brandCampaignDialogService.showAd({
      postLog: postLog as BrandPostLogWithAd,
      campaign: this.campaign,
      campaignDetails: this.campaign.currentDetails,
    });
  }
}
